import React, { useEffect, useState } from 'react';
import {
  Field,
  Image,
  ImageField,
  RichText,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Icons from 'components/atoms/Icons';
import { StyleguideComponentProps } from 'lib/component-props';

type SisterDetailsProps = StyleguideComponentProps & {
  fields: {
    Photo?: ImageField;
    Name?: Field<string>;
    Description?: Field<string>;
    Biography?: Field<string>;
    Story?: Field<string>;
  };
};

const SisterDetails = (props: SisterDetailsProps) => {
  const [shareUrl, setShareUrl] = useState('');
  const { fields } = props;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setShareUrl(window.location.href);
    }
  }, []);

  if (!fields) {
    return <div>No sister details available</div>;
  }

  const leaderName = fields.Name?.value || '';
  const portraitAlt = leaderName
    ? `Portrait of ${leaderName.replace(/<[^>]*>/g, '')}`
    : 'Portrait';
  const shareLinks = [
    {
      id: 'facebook-round',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      ariaLabel: 'Share on Facebook',
    },
    {
      id: 'instagram',
      href: 'https://www.instagram.com/osfhealthcare/',
      ariaLabel: 'Visit OSF Healthcare on Instagram',
    },
    {
      id: 'linkedin-round',
      href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}`,
      ariaLabel: 'Share on LinkedIn',
    },
  ];

  return (
    <article
      className="sister-details overhauled bg-primary"
      id={`sister-details-${props.rendering?.uid}`}
    >
      <div className="sister-details__inner">
        <div className="sister-details__hero">
          {fields.Name?.value && (
            <RichText
              tag="h1"
              className="osf-display-h1 sister-details__title"
              field={fields.Name}
            />
          )}

          {fields.Photo?.value?.src && (
            <figure className="sister-details__portrait">
              <Image field={fields.Photo} alt={portraitAlt} />
            </figure>
          )}

          {fields.Description?.value && (
            <RichText
              className="sister-details__intro osf-intro-copy"
              field={fields.Description}
            />
          )}
        </div>

        {fields.Biography?.value && (
          <RichText
            className="sister-details__biography"
            field={fields.Biography}
          />
        )}

        {fields.Story?.value && (
          <aside className="sister-details__story" aria-label="Featured story">
            <div className="sister-details__story-outer">
              <RichText
                className="sister-details__story-inner"
                field={fields.Story}
              />
            </div>
          </aside>
        )}

        <div className="share-bar">
          <h4 className="share-bar-title osf-button-label">Share this story</h4>
          <ul className="social-icons" aria-label="Share this story">
            {shareLinks.map(link => (
              <li key={link.id}>
                <a
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="social-icon"
                  aria-label={link.ariaLabel}
                >
                  <Icons id={link.id} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
};

export default SisterDetails;
